import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchUsers, fetchUserStatistics } from '../store/slices/usersSlice';
import EnhancedTable from '../components/common/EnhancedTable';
import AgentViewModal from '../components/dialogs/AgentViewModal';
import StatusChip from '../utils/StatusChip';
import { useDebounce } from '../hooks/useDebounce';

interface Agent {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  cellnumber?: string | null;
  agentcode?: string | null;
  roleId?: number;
  groupId?: string;
  userstatus?: string | number | null;
  created?: string;
  modified?: string;
  group?: {
    id?: string;
    name?: string;
  };
}

const UsersList: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const dispatch = useAppDispatch();
  const { users, loading, statistics, statisticsLoading, pagination } = useAppSelector((state: any) => state.users);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  // const [columnFilters, setColumnFilters] = useState({ name: '', phoneNumber: '', signupOtp: '', signinOtp: '', group: '', city: '', state: '', status: 'all' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [columnFilters, setColumnFilters] = useState({
      firstname: "",
      lastname: "",
      email: "",
      cellnumber: "",
      agentcode: "",
      userstatus: "all"
    });
  
  // Column reordering state
  const [columns, setColumns] = useState([
    {
      id: 'firstname',
      label: 'First Name',
      minWidth: 140,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.firstname || '-'}</Typography>
      ),
    },
    {
      id: 'lastname',
      label: 'Last Name',
      minWidth: 140,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.lastname || '-'}</Typography>
      ),
    },
    {
      id: 'groupname',
      label: 'Group Name',
      minWidth: 160,
      // sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.group?.name || '-'}</Typography>
      ),
    },
    {
      id: 'cellnumber',
      label: 'Cell Number',
      minWidth: 150,
      // sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.cellnumber || '-'}</Typography>
      ),
    },
    {
      id: 'agentcode',
      label: 'Agent Code',
      minWidth: 140,
      // sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.agentcode || '-'}</Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 180,
      // sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.email || '-'}</Typography>
      ),
    },
    {
      id: 'userstatus',
      label: 'Status',
      minWidth: 120,
      align: 'center' as const,
      // sortable: true,
      draggable: true,
      format: (value: number, row: Agent) => (
        <StatusChip status={row.userstatus ?? 0} />
      ),
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

    const debouncedFirstname = useDebounce(columnFilters.firstname, 500);
    const debouncedLastname = useDebounce(columnFilters.lastname, 500);
    const debouncedEmail = useDebounce(columnFilters.email, 500);
    const debouncedCellnumber = useDebounce(columnFilters.cellnumber, 500);
    const debouncedAgentcode = useDebounce(columnFilters.agentcode, 500);

  // Handle column reordering
  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    // Optionally save to localStorage for persistence
    localStorage.setItem('usersTableColumnOrder', JSON.stringify(newColumns.map((col: any) => col.id)));
  };

   const handleColumnFilterChange = (columnId: string, value: string) => {
    // Only allow numbers for cellnumber filter
    if (columnId === 'cellnumber') {
      // Remove non-numeric characters
      value = value.replace(/[^0-9]/g, '');
    }
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Load saved column order on component mount
  React.useEffect(() => {
    // Only run once on mount
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem('usersTableColumnOrder');
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds.map((id: string) => 
          columns.find(col => col.id === id)
        ).filter(Boolean);
        
        // Add any new columns that weren't in the saved order
        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter((col: any) => !existingIds.includes(col.id));
        
        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error('Error loading saved column order:', error);
      }
    }
  }, [columns]); // Now safe to include 'columns' in dependency array

  // Debounce for name filter (optional, can add for other filters)
  // import useDebounce if needed
  // const debouncedName = useDebounce(columnFilters.name, 500);

  // Load users with filters, pagination, sorting
  const loadUsers = React.useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
    };

    if(debouncedFirstname.trim()){
      params["search"] = debouncedFirstname;
    }
    if(debouncedLastname.trim()){
      params["search"] = debouncedLastname;
    }
    if(debouncedEmail.trim()){
      params["search"] = debouncedEmail;
    }
    if(debouncedCellnumber.trim()){
      params["search"] = debouncedCellnumber;
    }
    if(debouncedAgentcode.trim()){
      params["search"] = debouncedAgentcode;
    }

        if (
      columnFilters.userstatus &&
      columnFilters.userstatus !== "all"
    ) {
      params["where[userstatus]"] =
        columnFilters.userstatus === "active" ? 1 : 0;
    }
    // if (columnFilters.name) params.search = columnFilters.name;
    // if (columnFilters.status && columnFilters.status !== 'all') {
    //   params["where[status]"] = columnFilters.status === 'active' ? 1 : 0;
    // }
    // if (columnFilters.group) params["where[group]"] = columnFilters.group;
    // if (columnFilters.city) params["where[city]"] = columnFilters.city;
    if (sortField) params.sort = `${sortField} ${sortDirection}`;
    dispatch(fetchUsers(params));
  }, [dispatch, currentPage, itemsPerPage, sortField, sortDirection, debouncedFirstname, debouncedLastname, debouncedEmail, debouncedCellnumber, debouncedAgentcode, columnFilters.userstatus]);

  useEffect(() => {
    dispatch(fetchUserStatistics());
  }, [dispatch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleViewAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setViewModalOpen(true);
  };

  //  const handleColumnFilterChange = (columnId: string, value: string) => {
  //   setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
  //   setCurrentPage(1); // Reset to first page when filtering
  // };

  // Remove local filtering, use API data directly

  const stats = [
    {
      title: 'Total Agents',
      value: statistics.total,
      color: 'primary' as const,
      loading: statisticsLoading,
    },
    {
      title: 'Active Agents',
      value: statistics.active,
      color: 'success' as const,
      loading: statisticsLoading,
    },
    {
      title: 'Inactive Agents',
      value: statistics.deactive,
      color: 'error' as const,
      loading: statisticsLoading,
    },
  ];

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header Card */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: '#fff',
          boxShadow: '0 4px 18px -4px rgba(0,0,0,0.12)',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>Agents</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage registered agents.
          </Typography>
        </Box>
      </Box>

      {/* Neutral Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' },
          gap: 2.5,
          mb: 3,
        }}
      >
        {stats.map((stat, idx) => (
          <Card
            key={idx}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              background: '#fff',
              position: 'relative',
              overflow: 'hidden',
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  stat.color === 'primary'
                    ? 'linear-gradient(120deg,rgba(25,118,210,.06),transparent)'
                    : stat.color === 'success'
                    ? 'linear-gradient(120deg,rgba(46,125,50,.08),transparent)'
                    : 'linear-gradient(120deg,rgba(211,47,47,.08),transparent)',
                pointerEvents: 'none',
              },
            }}
          >
            <CardContent sx={{ py: 2.2, px: 2.4 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8, color: `${stat.color}.main` }}>
                {stat.title}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {stat.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Enhanced Table */}
      <EnhancedTable
        columns={columns}
        data={users}
        loading={loading}
        onView={handleViewAgent}
        showActions={true}
        emptyMessage="No agents found"
        enableColumnReordering={true}
        onColumnReorder={handleColumnReorder}
        columnFilters={columnFilters}
        onColumnFilterChange={handleColumnFilterChange}
        statusFilterOptions={{ status: [ { value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' } ] }}
        enableStickyFilters
        showSelection={false}
        useExternalPagination
        pagination={pagination}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(newItemsPerPage) => {
          setItemsPerPage(newItemsPerPage);
          setCurrentPage(1);
        }}
        useExternalSorting
        onSort={(field) => {
          if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
          } else {
            setSortField(field);
            setSortDirection('asc');
          }
          setCurrentPage(1);
        }}
        currentSortField={sortField}
        currentSortDirection={sortDirection}
        entityName="agent"
      />

      {/* Agent View Modal */}
      <AgentViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
      />

      {/* Delete Confirmation Dialog */}
      {/* Delete Confirmation Dialog */}
    </Box>
  );
};

export default UsersList;